import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Eye, CheckCircle2, XCircle, Hourglass, Send } from "lucide-react";
import { OtpLog, OtpStatus, OtpChannel } from "@/pages/otp-log/types/otp-log";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { useState } from "react";
import { OtpLogDetailModal } from "./otp-log-detail-modal";
import { useResendOtp } from "../hooks/use-otp-logs";

interface OtpLogTableProps {
  logs: OtpLog[];
  isLoading?: boolean;
}

const getStatusIcon = (status: OtpStatus) => {
  switch (status) {
    case OtpStatus.SUCCESS:
      return <CheckCircle2 className="h-5 w-5 text-green-600" />;
    case OtpStatus.FAILED:
      return <XCircle className="h-5 w-5 text-red-600" />;
    case OtpStatus.PENDING:
      return <Hourglass className="h-5 w-5 text-yellow-600" />;
    default:
      return null;
  }
};

export function OtpLogTable({ logs, isLoading }: OtpLogTableProps) {
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  const { mutate: resendOtp, isPending: isResending } = useResendOtp();

  if (isLoading) {
    return (
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-center">Date</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="text-center">Channel</TableHead>
              <TableHead className="text-center">User</TableHead>
              <TableHead className="text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[...Array(5)].map((_, i) => (
              <TableRow key={i}>
                <TableCell colSpan={5} className="h-12 animate-pulse bg-gray-100" />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-center">Date</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="text-center">Channel</TableHead>
              <TableHead className="text-center">User</TableHead>
              <TableHead className="text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  No OTP logs found
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="text-center font-medium">
                    {format(new Date(log.created_at), "MMM dd, yyyy HH:mm")}
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center">
                      {getStatusIcon(log.status)}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    {log.channel && log.channel !== OtpChannel.SMS ? (
                      <Badge variant="outline">{log.channel}</Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {log.user ? (
                      <div>
                        <div className="font-medium">{log.user.full_name}</div>
                        {log.user.preferred_email && (
                          <div className="text-sm text-muted-foreground">
                            {log.user.preferred_email}
                          </div>
                        )}
                      </div>
                    ) : log.otpless_identifier ? (
                      <span className="text-muted-foreground">
                        {log.otpless_identifier}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedLogId(log.id)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => resendOtp(log.id)}
                        disabled={isResending}
                      >
                        <Send className="h-4 w-4 mr-1" />
                        Resend
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {selectedLogId && (
        <OtpLogDetailModal
          logId={selectedLogId}
          open={!!selectedLogId}
          onClose={() => setSelectedLogId(null)}
        />
      )}
    </>
  );
}

